import React, { useMemo } from 'react';
import { Box, Button, Dialog, Tooltip } from '@attain-sre/attain-design-system';
import {
  COMPLIANCE_WARNING_AUTHORISED_ONLY_TOOLTIP,
  COMPLIANCE_WARNING_CANCEL_EXPORT_BUTTON,
  COMPLIANCE_WARNING_CONTINUE_BUTTON,
  COMPLIANCE_WARNING_EMPTY_CELL,
  COMPLIANCE_WARNING_MODAL_TITLE,
  COMPLIANCE_WARNING_PRIMARY_EMPHASIS,
  COMPLIANCE_WARNING_PRIMARY_LEAD,
  COMPLIANCE_WARNING_SUB_TEXT,
  COMPLIANCE_WARNING_TABLE_ARIA_LABEL,
  COMPLIANCE_WARNING_TABLE_COL_CATEGORY,
  COMPLIANCE_WARNING_TABLE_COL_MATCHED,
  COMPLIANCE_WARNING_TABLE_COL_POLICY,
  COMPLIANCE_WARNING_TABLE_EMPTY,
} from '../../content';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as Styled from './ComplianceWarningModal.styles';

export interface ComplianceWarningModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  data: Array<{
    category: string;
    /** Display label for policy (e.g. Full ban / Partial ban). */
    policy_label: string;
    matched_keywords: string[];
    matched_brands: string[];
  }>;
}

function formatMatchedValues(row: {
  matched_keywords: string[];
  matched_brands: string[];
}): string {
  const map = new Map<string, string>();
  [...row.matched_keywords, ...row.matched_brands]
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter(Boolean)
    .forEach((s) => {
      const key = s.toLowerCase();
      if (!map.has(key)) {
        map.set(key, s); // preserve first occurrence casing
      }
    });

  return Array.from(map.values()).join(', ');
}

const ComplianceWarningModal = ({
  open,
  onClose,
  onCancel,
  onConfirm,
  data,
}: ComplianceWarningModalProps) => {
  const { uiAudienceManagerAudienceComplianceAuthorisedUsers = false } =
    useFlags();

  const tableRows = useMemo(
    () =>
      data.map((row, index) => ({
        ...row,
        displayValues: formatMatchedValues(row),
        key: `${row.category}-${row.policy_label}-${index}`,
      })),
    [data]
  );

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="compliance-warning-title"
      aria-describedby="compliance-warning-description"
      headerID="compliance-warning-title"
      header={
        <Styled.CardHeader>
          <Styled.CardTitle id="compliance-warning-title">
            {COMPLIANCE_WARNING_MODAL_TITLE}
          </Styled.CardTitle>
        </Styled.CardHeader>
      }
      bodyID="compliance-warning-description"
      body={
        <Box>
          <Styled.PrimaryText id="compliance-warning-description">
            {COMPLIANCE_WARNING_PRIMARY_LEAD}{' '}
            <Styled.Emphasis>
              {COMPLIANCE_WARNING_PRIMARY_EMPHASIS}
            </Styled.Emphasis>
          </Styled.PrimaryText>
          <Styled.TableScrollRegion
            role="region"
            aria-label={COMPLIANCE_WARNING_TABLE_ARIA_LABEL}
            tabIndex={0}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <Styled.HeaderTableCell
                    component="th"
                    scope="col"
                    width="30%"
                  >
                    {COMPLIANCE_WARNING_TABLE_COL_CATEGORY}
                  </Styled.HeaderTableCell>
                  <Styled.HeaderTableCell component="th" scope="col">
                    {COMPLIANCE_WARNING_TABLE_COL_MATCHED}
                  </Styled.HeaderTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      {COMPLIANCE_WARNING_TABLE_EMPTY}
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>{row.category}</TableCell>
                      <TableCell>
                        {row.displayValues || COMPLIANCE_WARNING_EMPTY_CELL}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Styled.TableScrollRegion>
          <Styled.SubText>{COMPLIANCE_WARNING_SUB_TEXT}</Styled.SubText>
        </Box>
      }
      footer={
        <Styled.ModalFooterActions>
          <Button color="primary" onClick={onCancel} variant="outlined">
            {COMPLIANCE_WARNING_CANCEL_EXPORT_BUTTON}
          </Button>
          {uiAudienceManagerAudienceComplianceAuthorisedUsers ? (
            <Button
              color="primary"
              disableElevation
              onClick={onConfirm}
              variant="contained"
            >
              {COMPLIANCE_WARNING_CONTINUE_BUTTON}
            </Button>
          ) : (
            <Tooltip
              title={COMPLIANCE_WARNING_AUTHORISED_ONLY_TOOLTIP}
              placement="top"
              arrow
            >
              <span>
                <Button
                  color="primary"
                  disableElevation
                  disabled
                  variant="contained"
                >
                  {COMPLIANCE_WARNING_CONTINUE_BUTTON}
                </Button>
              </span>
            </Tooltip>
          )}
        </Styled.ModalFooterActions>
      }
    />
  );
};

export default ComplianceWarningModal;
